import { Form, Formik } from 'formik';
import React from 'react';
import toast from 'react-hot-toast';
import * as Yup from 'yup';

import { useEventTracking } from '@hooks';
import Modal from '@mui/material/Modal';
import { edit_profile_settings_path } from '@routes';
import { ClickTarget, TrackingLocation, click } from '@tracking';
import { createMessage } from '@utils/backend-api/messages';

import { PrimaryButton, TextButton } from './Button/variants';
import TextAreaField from './Form/TextAreaField';
import IconClose from './Images/Rebranding/IconClose';

const validationSchema = () =>
  Yup.object({
    message: Yup.string().required('A message is required'),
  });

interface MessageModalUser {
  id: number;
  name: string;
  requiresMessageEnable?: boolean;
  currentUserId?: number;
}

interface MessageModalProps {
  user: MessageModalUser;
  onClose: () => void;
}

const SEND_MESSAGE = 'Send Message';

const MessageModal = ({ user, onClose }: MessageModalProps) => {
  const { trackEvent } = useEventTracking({
    location: TrackingLocation.Modal,
  });

  const onSubmit = async ({ message }) => {
    try {
      await createMessage(user.id, message);
      onClose();
      toast.success('Your message has been sent!');
      trackEvent(
        click({ clickTarget: ClickTarget.Send, clickText: SEND_MESSAGE, contentId: user.id })
      );
    } catch (e) {
      console.error(e);
      toast.error('Message was not sent, please try again.');
    }
  };

  return (
    <Modal
      open={!!user}
      onClose={onClose}
      disableScrollLock={true}
      className="fixed bottom-0 left-0 right-0 top-0 flex items-center justify-center"
    >
      <div className="relative max-w-[400px] bg-white px-24 pb-36 pt-8">
        <IconClose
          className="absolute right-8 top-8 h-10 cursor-pointer"
          strokeWidth={2}
          onClick={onClose}
        />
        {!!user && (
          <Formik
            onSubmit={onSubmit}
            initialValues={{ message: '' }}
            validationSchema={validationSchema}
          >
            {({ isSubmitting, isValid }) => (
              <Form className="flex flex-col">
                <label
                  className="mb-8 font-suisse-intl-mono text-caption-sm text-black"
                  htmlFor="message"
                >
                  Message {user?.name}
                </label>
                {user.requiresMessageEnable && (
                  <p className="mb-8 mt-0 font-suisse-intl-mono text-caption-sm text-grayscale-gray">
                    Enable user profile and messaging from your{' '}
                    <TextButton
                      as="link"
                      className="!inline-block font-suisse-intl-mono text-caption-sm text-grayscale-gray"
                      href={edit_profile_settings_path(user.currentUserId)}
                    >
                      profile settings
                    </TextButton>{' '}
                    to send a message.
                  </p>
                )}
                <TextAreaField
                  name="message"
                  autoComplete="off"
                  placeholder="Enter a message"
                  disabled={user.requiresMessageEnable}
                  inputClassName="h-[200px] border-2 border-solid border-grayscale-charcoal"
                />
                <p className="mb-16 mt-8 font-suisse-screen text-disclosure-sm text-grayscale-gray">
                  Note: Your email address will appear in the recipient&apos;s message. Do not send
                  commercial promotions or spam to people using The Information&apos;s messaging
                  system. If you do, you may lose access to this function and/or have your account
                  deactivated.
                </p>
                <PrimaryButton
                  as="button"
                  type="submit"
                  size="small"
                  fullWidth
                  disabled={isSubmitting || !isValid || user.requiresMessageEnable}
                >
                  {SEND_MESSAGE}
                </PrimaryButton>
              </Form>
            )}
          </Formik>
        )}
      </div>
    </Modal>
  );
};

export default MessageModal;
